import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { submitFormData } from '../../../pages/api';
import { FaTrash } from 'react-icons/fa';
import apiClient from '../../../pages/keycloak/axiosInstance';

const Form_19 = () => {
  const navigate = useNavigate();
  const fetchedProblems = useRef(new Set());

  const inspectionDate = sessionStorage.getItem('inspectionDate');
  const inspectionShift = sessionStorage.getItem('inspectionShift');
  const inspectorName = sessionStorage.getItem('username');
  const savedData = sessionStorage.getItem('RootCauseAnalysisF19formData');
  const [formData, setFormData] = useState(() => {
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return {
          ...parsed,
          problems: Array.isArray(parsed.problems)
            ? parsed.problems.map((problem) => ({
                ...problem,
                vendorEvidence: Array.isArray(problem.vendorEvidence) ? problem.vendorEvidence : [],
                inspectorEvidence: Array.isArray(problem.inspectorEvidence) ? problem.inspectorEvidence : [],
              }))
            : [], // Default to empty array if parsed.problems is not an array
        };
      } catch (error) {
        console.error('Error parsing savedData:', error);
        // Fallback to default state if JSON parsing fails
        return {
          date: inspectionDate || '',
          shift: inspectionShift || '',
          inspectorName: inspectorName || '',
          problems: [
            {
              id: Date.now(),
              description: '',
              category: '',
              initiationDate: '',
              rootCause: '',
              correctiveAction: '',
              preventiveAction: '',
              targetDate: '',
              closingDate: '',
              delayedDays: '',
              status: '',
              manufacturingLine: '',
              vendorEvidence: [],
              inspectorEvidence: [],
            },
          ],
        };
      }
    }
    // Default state if no savedData
    return {
      date: inspectionDate || '',
      shift: inspectionShift || '',
      inspectorName: inspectorName || '',
      problems: [
        {
          id: Date.now(),
          description: '',
          category: '',
          initiationDate: '',
          rootCause: '',
          correctiveAction: '',
          preventiveAction: '',
          targetDate: '',
          closingDate: '',
          delayedDays: '',
          status: '',
          manufacturingLine: '',
          vendorEvidence: [],
          inspectorEvidence: [],
        },
      ],
    };
  });  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Parse inspectionData on mount
  useEffect(() => {
    const inspectionData = sessionStorage.getItem('inspectionData');
    if (inspectionData) {
      try {
        const parsedInspectionData = JSON.parse(inspectionData);
        if (parsedInspectionData.RootCauseAnalysis) {
          setFormData((prev) => ({
            ...prev,
            date: parsedInspectionData.RootCauseAnalysis.date || prev.date,
            shift: parsedInspectionData.RootCauseAnalysis.shift || prev.shift,
            inspectorName: parsedInspectionData.RootCauseAnalysis.inspectorName || prev.inspectorName,
            problems: parsedInspectionData.RootCauseAnalysis.problems?.map((problem, index) => ({
              ...problem,
              id: prev.problems[index]?.id || Date.now() + index,
              vendorEvidence: Array.isArray(problem.vendorEvidence) ? problem.vendorEvidence : [],
              inspectorEvidence: Array.isArray(problem.inspectorEvidence) ? problem.inspectorEvidence : [],
              delayedDays: problem.initiationDate && problem.targetDate
                ? calculateDelayedDays(problem.initiationDate, problem.targetDate)
                : problem.delayedDays || '',
            })) || prev.problems.map((problem) => ({
              ...problem,
              vendorEvidence: Array.isArray(problem.vendorEvidence) ? problem.vendorEvidence : [],
              inspectorEvidence: Array.isArray(problem.inspectorEvidence) ? problem.inspectorEvidence : [],
            })),
          }));
        }
      } catch (error) {
        console.error('Error parsing inspectionData:', error);
      }
    }
  
    // Reset file fields to avoid stale data
    setFormData((prev) => ({
      ...prev,
      date: inspectionDate || prev.date,
      shift: inspectionShift || prev.shift,
      inspectorName: inspectorName || prev.inspectorName,
      problems: prev.problems.map((problem) => ({
        ...problem,
        vendorEvidence: Array.isArray(problem.vendorEvidence) ? problem.vendorEvidence : [],
        inspectorEvidence: Array.isArray(problem.inspectorEvidence) ? problem.inspectorEvidence : [],
      })),
    }));
  }, []);

  // Save formData to sessionStorage
  useEffect(() => {
    const sanitizedFormData = {
      ...formData,
      problems: formData.problems.map((problem) => ({
        ...problem,
        // vendorEvidence: Array.isArray(problem.vendorEvidence) ? problem.vendorEvidence : [],
        // inspectorEvidence: Array.isArray(problem.inspectorEvidence) ? problem.inspectorEvidence : [],
      })),
    };
    sessionStorage.setItem('RootCauseAnalysisF19formData', JSON.stringify(sanitizedFormData));
  }, [formData]);

  // Fetch uploaded files for specific problems
  const fetchUploadedFiles = useCallback(
    async (problemIndices = []) => {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
      if (!projectId || !inspectionDate || !inspectionSupplier || !inspectionShift) {
        console.warn('Missing parameters for fetching files');
        return;
      }

      const newFetchedFiles = {};
      await Promise.all(
        problemIndices.flatMap((problemIndex) =>
          [
            { type: 'vendorEvidence', fieldName: `problem${problemIndex}VendorEvidence` },
            { type: 'inspectorEvidence', fieldName: `problem${problemIndex}InspectorEvidence` },
          ].map(async ({ fieldName }) => {
            try {
              const response = await apiClient.get(
                `get_uploaded_inspection_files`,
                {
                  params: {
                    projectId,
                    inspectionSupplier,
                    inspectionDate,
                    inspectionShift,
                    formName: 'RootCauseAnalysis',
                    inputFieldId: fieldName,
                  },
                }
              );
              newFetchedFiles[fieldName] = response.data.files || [];
            } catch (error) {
              console.error(`Error fetching files for ${fieldName}:`, error);
              newFetchedFiles[fieldName] = [];
            }
          })
        )
      );

      setUploadedFiles((prev) => ({
        ...prev,
        ...newFetchedFiles,
      }));
    },
    [inspectionDate, inspectionShift]
  );

  // Initial fetch for existing problems
  useEffect(() => {
    const problemIndices = formData.problems
      .map((_, index) => index + 1)
      .filter((index) => !fetchedProblems.current.has(index));
    if (problemIndices.length > 0) {
      problemIndices.forEach((index) => fetchedProblems.current.add(index));
      fetchUploadedFiles(problemIndices);
    }
  }, []);

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
    });

  const handleFileChange = async (index, e, type) => {
    const files = Array.from(e.target.files);
    const fieldName = `problem${index + 1}${type === 'vendor' ? 'Vendor' : 'Inspector'}Evidence`;
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    const invalidFiles = files.filter((file) => !validTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      setErrors((prev) => ({ ...prev, [fieldName]: 'Only PDF, JPEG, or PNG files allowed.' }));
      return;
    }

    try {
      const fileData = await Promise.all(
        files.map(async (file) => ({
          file: await convertToBase64(file),
          filename: file.name,
        }))
      );
      const newProblems = formData.problems.map((problem, i) =>
        i === index
          ? {
              ...problem,
              [type === 'vendor' ? 'vendorEvidence' : 'inspectorEvidence']: fileData,
            }
          : problem
      );
      setFormData((prev) => ({ ...prev, problems: newProblems }));
      setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [fieldName]: 'Error processing files.' }));
    }
  };

  const calculateDelayedDays = (initiationDate, targetDate) => {
    if (!initiationDate || !targetDate) return '';
    const initDate = new Date(initiationDate);
    const targDate = new Date(targetDate);
    const diffTime = targDate - initDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays.toString() : '0';
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProblemChange = (index, { target: { name, value } }) => {
    const newProblems = formData.problems.map((problem, i) => {
      if (i === index) {
        const updatedProblem = { ...problem, [name]: value };
        if (name === 'initiationDate' || name === 'targetDate') {
          updatedProblem.delayedDays = calculateDelayedDays(
            updatedProblem.initiationDate,
            updatedProblem.targetDate
          );
        }
        return updatedProblem;
      }
      return problem;
    });
    setFormData((prev) => ({ ...prev, problems: newProblems }));
  };

  const addProblemForm = () => {
    const newProblemIndex = (Array.isArray(formData.problems) ? formData.problems.length : 0) + 1;
    setFormData((prev) => ({
      ...prev,
      problems: [
        ...(Array.isArray(prev.problems) ? prev.problems : []),
        {
          id: Date.now(),
          description: '',
          category: '',
          initiationDate: '',
          rootCause: '',
          correctiveAction: '',
          preventiveAction: '',
          targetDate: '',
          closingDate: '',
          delayedDays: '',
          status: '',
          manufacturingLine: '',
          vendorEvidence: [],
          inspectorEvidence: [],
        },
      ],
    }));
    fetchUploadedFiles([newProblemIndex]);
    fetchedProblems.current.add(newProblemIndex);
  };
  const deleteProblemForm = (index) => {
    const deletedProblemIndex = index + 1;
    setFormData((prev) => ({
      ...prev,
      problems: prev.problems.filter((_, i) => i !== index),
    }));
    setUploadedFiles((prev) => {
      const updatedFiles = { ...prev };
      delete updatedFiles[`problem${deletedProblemIndex}VendorEvidence`];
      delete updatedFiles[`problem${deletedProblemIndex}InspectorEvidence`];
      return updatedFiles;
    });
    fetchedProblems.current.delete(deletedProblemIndex);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    try {
      const projectId = localStorage.getItem('selectedProject');
      const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');
  
      for (let index = 0; index < formData.problems.length; index++) {
        const problem = formData.problems[index];
        for (const type of ['vendorEvidence', 'inspectorEvidence']) {
          if (Array.isArray(problem[type]) && problem[type].length > 0) {
            const fieldName = `problem${index + 1}${type === 'vendorEvidence' ? 'Vendor' : 'Inspector'}Evidence`;
            const filesToUpload = problem[type].map((fileData) => ({
              inputFieldId: fieldName,
              file: fileData.file,
              filename: fileData.filename,
            }));
            await apiClient.post(`upload_inspection_files`, {
              projectId,
              inspectionSupplier,
              inspectionDate,
              inspectionShift,
              inspectorName,
              formName: 'RootCauseAnalysis',
              files: filesToUpload,
            });
  
            const response = await apiClient.get(
              `get_uploaded_inspection_files`,
              {
                params: {
                  projectId,
                  inspectionSupplier,
                  inspectionDate,
                  inspectionShift,
                  formName: 'RootCauseAnalysis',
                  inputFieldId: fieldName,
                },
              }
            );
            setUploadedFiles((prev) => ({
              ...prev,
              [fieldName]: response.data.files || [],
            }));
          }
        }
      }
  
      const payload = {
        projectId,
        inspectionSupplier,
        inspectionDate: formData.date,
        inspectionShift: formData.shift,
        inspectorName: formData.inspectorName,
        status: 'completed',
        RootCauseAnalysis: {
          date: formData.date,
          shift: formData.shift,
          inspectorName: formData.inspectorName,
          problems: formData.problems.map((problem) => ({
            description: problem.description || '',
            category: problem.category || '',
            initiationDate: problem.initiationDate || '',
            rootCause: problem.rootCause || '',
            correctiveAction: problem.correctiveAction || '',
            preventiveAction: problem.preventiveAction || '',
            targetDate: problem.targetDate || '',
            closingDate: problem.closingDate || '',
            delayedDays: problem.delayedDays || '',
            status: problem.status || '',
            manufacturingLine: problem.manufacturingLine || '',
            vendorEvidence: Array.isArray(problem.vendorEvidence) && problem.vendorEvidence.length > 0
              ? problem.vendorEvidence.map((fileData) => ({ filename: fileData.filename }))
              : [],
            inspectorEvidence: Array.isArray(problem.inspectorEvidence) && problem.inspectorEvidence.length > 0
              ? problem.inspectorEvidence.map((fileData) => ({ filename: fileData.filename }))
              : [],
          })),
        },
      };
  
      await submitFormData(payload);
      setFormData((prev) => ({
        ...prev,
        problems: prev.problems.map((problem) => ({
          ...problem,
          vendorEvidence: [],
          inspectorEvidence: [],
        })),
      }));
      alert('Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error.response?.data || error.message);
      alert(`Failed to submit form: ${error.response?.data?.error || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const problemFields = [
    { name: 'description', label: 'Description', type: 'text' },
    { name: 'category', label: 'Category', type: 'text' },
    { name: 'initiationDate', label: 'Initiation Date', type: 'date' },
    { name: 'rootCause', label: 'Root Cause', type: 'text' },
    { name: 'correctiveAction', label: 'Corrective Action', type: 'text' },
    { name: 'preventiveAction', label: 'Preventive Action', type: 'text' },
    { name: 'targetDate', label: 'Target Date', type: 'date' },
    { name: 'closingDate', label: 'Closing Date', type: 'date' },
    { name: 'delayedDays', label: 'Delayed Days', type: 'text', readOnly: true },
    { name: 'status', label: 'Status', type: 'text' },
    { name: 'manufacturingLine', label: 'Manufacturing Line', type: 'text' },
  ];

  return (
    <div className="max-w-screen mx-auto">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-6">
        <h1 className="text-xl font-bold text-gray-800">Root Cause Analysis</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { name: 'date', label: 'Date', type: 'date' },
            { name: 'shift', label: 'Shift', type: 'text', readOnly: true },
            { name: 'inspectorName', label: 'Inspector Name', type: 'text', readOnly: true },
          ].map((field, index) => (
            <div key={index}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 capitalize">
                {field.label}
              </label>
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                readOnly={field.readOnly}
              />
            </div>
          ))}
        </div>
        <div className="space-y-8">
          <h2 className="text-lg font-semibold text-gray-800">Problem Details</h2>
          {formData.problems.map((problem, index) => (
            <div key={problem.id} className="border border-gray-300 p-6 rounded-lg space-y-6 relative">
              <h3 className="text-md font-medium text-gray-700">Problem {index + 1}</h3>
              {formData.problems.length > 1 && (
                <button
                  type="button"
                  onClick={() => deleteProblemForm(index)}
                  className="absolute top-4 right-4 text-red-600 hover:text-red-800"
                  title="Delete this problem"
                >
                  <FaTrash />
                </button>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {problemFields.map((field, i) => (
                  <div key={i}>
                    <label
                      htmlFor={`problem-${problem.id}-${field.name}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      id={`problem-${problem.id}-${field.name}`}
                      name={field.name}
                      value={problem[field.name]}
                      onChange={(e) => handleProblemChange(index, e)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      readOnly={field.readOnly}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-500 p-4 rounded">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Vendor Evidence</h4>
                  <div className="relative">
                    <input
                      type="file"
                      multiple
                      id={`vendorEvidence-${problem.id}`}
                      onChange={(e) => handleFileChange(index, e, 'vendor')}
                      className={`block w-full text-sm border rounded-md shadow-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#424752] file:text-white hover:file:bg-gray-600 ${
                        errors[`problem${index + 1}VendorEvidence`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {problem.vendorEvidence.length > 0 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-xs font-medium">
                        ✓ Uploaded
                      </span>
                    )}
                  </div>
                  {errors[`problem${index + 1}VendorEvidence`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`problem${index + 1}VendorEvidence`]}</p>
                  )}
                  {problem.vendorEvidence.length > 0 && (
                    <div className="text-sm text-gray-500 mt-1">
                      <p>Uploaded Files:</p>
                      <ul>
                        {problem.vendorEvidence.map((fileData, idx) => (
                          <li key={idx}>{fileData.filename}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {uploadedFiles[`problem${index + 1}VendorEvidence`]?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-semibold">Previously Uploaded:</p>
                      <ul className="text-xs text-gray-600">
                        {uploadedFiles[`problem${index + 1}VendorEvidence`].map((file, i) => (
                          <li key={i}>
                            {file.url && file.url.trim() ? (
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {file.filename}
                              </a>
                            ) : (
                              <span>{file.filename} (No URL)</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Inspector Evidence</h4>
                  <div className="relative">
                    <input
                      type="file"
                      id={`inspectorEvidence-${problem.id}`}
                      multiple
                      onChange={(e) => handleFileChange(index, e, 'inspector')}
                      className={`block w-full text-sm border rounded-md shadow-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#424752] file:text-white hover:file:bg-gray-600 ${
                        errors[`problem${index + 1}InspectorEvidence`] ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {problem.inspectorEvidence.length > 0 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 text-xs font-medium">
                        ✓ Uploaded
                      </span>
                    )}
                  </div>
                  {errors[`problem${index + 1}InspectorEvidence`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`problem${index + 1}InspectorEvidence`]}</p>
                  )}
                  {problem.inspectorEvidence.length > 0 && (
                    <div className="text-sm text-gray-500 mt-1">
                      <p>Uploaded Files:</p>
                      <ul>
                        {problem.inspectorEvidence.map((fileData, idx) => (
                          <li key={idx}>{fileData.filename}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {uploadedFiles[`problem${index + 1}InspectorEvidence`]?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-semibold">Previously Uploaded:</p>
                      <ul className="text-xs text-gray-600">
                        {uploadedFiles[`problem${index + 1}InspectorEvidence`].map((file, i) => (
                          <li key={i}>
                            {file.url && file.url.trim() ? (
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {file.filename}
                              </a>
                            ) : (
                              <span>{file.filename} (No URL)</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between space-x-4">
          <button
            type="button"
            onClick={addProblemForm}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#424752] hover:bg-gray-600"
          >
            Add Problem
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#424752] hover:bg-gray-600 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Form_19;